import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Course, CourseProgress } from '../types';
import { coursesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

// Add custom styles for line clamping
const customStyles = `
  .line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

const CourseDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState<Course | null>(null);
  const [progress, setProgress] = useState<CourseProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      
      try {
        const courseData = await coursesAPI.getCourse(parseInt(id));
        setCourse(courseData);
        
        // If user is logged in, check enrollment and get progress
        if (user) {
          try {
            const progressData = await coursesAPI.getCourseProgress(parseInt(id));
            setProgress(progressData);
            setIsEnrolled(true);
          } catch (error: any) {
            // User might not be enrolled yet
            if (error.response?.status === 403) {
              setIsEnrolled(false);
            } else {
              console.error('Error fetching progress:', error);
              setIsEnrolled(false);
            }
          }
        }
      } catch (error: any) {
        setError('Failed to load course details');
        console.error('Error fetching course:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, user]);

  const handleEnroll = async () => {
    if (!course || !user) return;
    
    try {
      await coursesAPI.enrollInCourse(course.id);
      setIsEnrolled(true);
      
      // Get initial progress
      const progressData = await coursesAPI.getCourseProgress(course.id);
      setProgress(progressData);
    } catch (error: any) {
      console.error('Failed to enroll:', error);
      
      // Check if already enrolled
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('Already enrolled')) {
        setIsEnrolled(true);
        // Get progress for already enrolled user
        try {
          const progressData = await coursesAPI.getCourseProgress(course.id);
          setProgress(progressData);
        } catch (progressError) {
          console.error('Failed to get progress:', progressError);
        }
      } else {
        alert('Failed to enroll in course');
      }
    }
  };

  const handleStartResumeCourse = () => {
    if (!course) return;
    
    if (progress?.next_lesson) {
      // Resume from next lesson
      navigate(`/courses/${course.id}/lessons/${progress.next_lesson}`);
    } else if (course.lessons.length > 0) {
      // Start from first lesson
      const firstLesson = course.lessons.sort((a, b) => a.order - b.order)[0];
      navigate(`/courses/${course.id}/lessons/${firstLesson.id}`);
    }
  };



  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-medical-50 to-tech-50 flex items-center justify-center">
        <div className="text-center">
          <div className="mb-8">
            <img 
              src="/logo-icon.svg" 
              alt="SC△NMEDX"
              className="w-16 h-16 mx-auto mb-4 animate-pulse"
            />
          </div>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-medical-500 mb-6 mx-auto"></div>
          <p className="text-lg text-gray-700 font-medium">Loading course...</p>
        </div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Course Not Found</h1>
          <p className="text-gray-600 mb-8">{error || 'The course you are looking for does not exist.'}</p>
          <Link
            to="/"
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold"
          >
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  // Sort lessons by order
  const sortedLessons = [...course.lessons].sort((a, b) => a.order - b.order);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Inject custom styles */}
      <style dangerouslySetInnerHTML={{ __html: customStyles }} />
      
      {/* Sidebar Navigation */}
      <>
        {/* Sidebar Overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
        
        {/* Sidebar */}
        <div className={`fixed left-0 top-0 h-full w-72 sm:w-80 bg-white border-r border-gray-200 z-50 transform transition-transform duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}>
          <div className="flex flex-col h-full">
            {/* Sidebar Header */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-lg overflow-hidden">
                  <img 
                    src="/logo-icon.svg" 
                    alt="SC△NMEDX"
                    className="w-full h-full object-contain"
                  />
                </div>
                <h2 className="text-base sm:text-lg font-semibold text-gray-900">Course Overview</h2>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Course Info */}
            <div className="p-4 sm:p-6 border-b border-tech-100">
              <div className="mb-4">
                <h3 className="font-display text-base sm:text-lg font-bold text-tech-900 mb-2 line-clamp-2">{course.title}</h3>
                <p className="text-xs sm:text-sm text-tech-600 line-clamp-3 mb-4">{course.description}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-4">
                <div className="bg-medical-50 rounded-xl p-2 sm:p-3 text-center">
                  <div className="text-xs text-medical-600 font-medium">👩‍🏫 Instructor</div>
                  <div className="text-xs sm:text-sm font-semibold text-medical-800 truncate">{course.instructor?.full_name || 'Expert'}</div>
                </div>
                <div className="bg-tech-50 rounded-xl p-2 sm:p-3 text-center">
                  <div className="text-xs text-tech-600 font-medium">📦 Lessons</div>
                  <div className="text-xs sm:text-sm font-semibold text-tech-800">{sortedLessons.length} modules</div>
                </div>
              </div>
              
              {/* Price and Enrollment */}
              {!isEnrolled && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4 mb-4">
                  <div className="text-xl sm:text-2xl font-bold text-green-600 mb-2">
                    ${course.price}
                  </div>
                  {user ? (
                    <button
                      onClick={handleEnroll}
                      className="w-full bg-green-600 hover:bg-green-700 text-white px-3 py-2 sm:px-4 sm:py-2 rounded-lg font-medium transition-colors text-sm"
                    >
                      Enroll Now
                    </button>
                  ) : (
                    <Link
                      to="/login"
                      className="block w-full bg-green-600 hover:bg-green-700 text-white px-3 py-2 sm:px-4 sm:py-2 rounded-lg font-medium transition-colors text-center text-sm"
                    >
                      Login to Enroll
                    </Link>
                  )}
                </div>
              )}

              {/* Start/Resume Course */}
              {isEnrolled && (
                <button
                  onClick={handleStartResumeCourse}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 sm:px-4 sm:py-2 rounded-lg font-medium transition-colors mb-4 text-sm"
                >
                  {progress?.completed_lessons ? 'Resume Course' : 'Start Course'}
                </button>
              )}
            </div>

            {/* Progress Overview */}
            {isEnrolled && progress && (
              <div className="px-4 sm:px-6 py-4 sm:py-6 border-b border-tech-100">
                <div className="bg-gradient-to-br from-medical-50 to-tech-50 rounded-2xl p-3 sm:p-4 border border-medical-200">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 bg-medical-500 rounded-lg flex items-center justify-center">
                        <svg className="w-2 h-2 sm:w-3 sm:h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                      </div>
                      <span className="text-xs sm:text-sm font-bold text-tech-800">📈 Your Progress</span>
                    </div>
                    <div className="bg-medical-500 text-white text-xs font-bold px-2 py-1 sm:px-3 sm:py-1 rounded-full">
                      {progress.progress_percentage.toFixed(0)}%
                    </div>
                  </div>
                  <div className="w-full bg-white/60 rounded-full h-2 sm:h-3 shadow-inner mb-3">
                    <div 
                      className="bg-gradient-to-r from-medical-400 via-medical-500 to-tech-500 h-2 sm:h-3 rounded-full transition-all duration-500 shadow-sm"
                      style={{ width: `${progress.progress_percentage}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs text-tech-600">
                    <span className="font-medium">✅ {progress.completed_lessons} completed</span>
                    <span className="font-medium">📚 {progress.total_lessons} total</span>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Lessons Navigation */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-4 sm:p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="w-4 h-4 sm:w-5 sm:h-5 bg-tech-500 rounded-lg flex items-center justify-center">
                    <svg className="w-2 h-2 sm:w-3 sm:h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <h4 className="text-xs sm:text-sm font-bold text-tech-800">🚀 Quick Navigation</h4>
                </div>
                <div className="space-y-2 sm:space-y-3">
                  {sortedLessons.slice(0, 8).map((lesson, index) => {
                    const isCompleted = progress ? progress.completed_lesson_ids.includes(lesson.id) : false;
                    const isCurrentLesson = progress?.next_lesson === lesson.id;
                    const isAccessible = isEnrolled;
                    
                    return (
                      <div key={lesson.id} className={`group ${!isAccessible ? 'opacity-60' : ''}`}>
                        <div className={`p-2 sm:p-3 rounded-xl sm:rounded-2xl border-2 transition-all duration-200 ${
                          isCompleted 
                            ? 'bg-gradient-to-r from-green-50 to-green-100 border-green-300 hover:border-green-400' 
                            : isCurrentLesson
                              ? 'bg-gradient-to-r from-medical-50 to-tech-50 border-medical-400 hover:border-medical-500'
                              : isAccessible
                                ? 'bg-tech-50 border-tech-200 hover:border-tech-300'
                                : 'bg-tech-25 border-tech-100'
                        }`}>
                          <div className="flex items-center space-x-2 sm:space-x-3">
                            {/* Enhanced Status Indicator */}
                            <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-lg sm:rounded-xl flex items-center justify-center text-xs font-bold shadow-sm ${
                              isCompleted 
                                ? 'bg-gradient-to-br from-green-500 to-green-600 text-white' 
                                : isCurrentLesson
                                  ? 'bg-gradient-to-br from-medical-500 to-medical-600 text-white'
                                  : isAccessible
                                    ? 'bg-gradient-to-br from-tech-200 to-tech-300 text-tech-700'
                                    : 'bg-tech-100 text-tech-500'
                            }`}>
                              {isCompleted ? (
                                <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              ) : isCurrentLesson ? (
                                <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1.586a1 1 0 01.707.293l2.414 2.414a1 1 0 00.707.293H15" />
                                </svg>
                              ) : (
                                <span className="text-xs">{lesson.order}</span>
                              )}
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <span className={`text-xs sm:text-sm font-medium truncate ${
                                  isCurrentLesson ? 'text-medical-800' : 
                                  isCompleted ? 'text-green-800' :
                                  isAccessible ? 'text-tech-800' : 'text-tech-600'
                                }`}>
                                  {lesson.title}
                                </span>
                                
                                {/* Status Chips */}
                                <div className="flex items-center space-x-1 ml-2">
                                  {isCompleted && (
                                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                  )}
                                  {isCurrentLesson && (
                                    <div className="w-2 h-2 bg-medical-500 rounded-full animate-pulse"></div>
                                  )}
                                  {!isAccessible && (
                                    <svg className="w-3 h-3 text-tech-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                  )}
                                </div>
                              </div>
                              
                              {/* Progress Indicator */}
                              <div className="mt-1">
                                <div className="flex items-center space-x-2 text-xs">
                                  {isCompleted && (
                                    <span className="text-green-600 font-medium">🎯 Completed</span>
                                  )}
                                  {isCurrentLesson && (
                                    <span className="text-medical-600 font-medium">▶ In Progress</span>
                                  )}
                                  {!isAccessible && (
                                    <span className="text-tech-500">🔒 Locked</span>
                                  )}
                                  {isAccessible && !isCompleted && !isCurrentLesson && (
                                    <span className="text-tech-600">📖 Ready</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  
                  {sortedLessons.length > 8 && (
                    <div className="text-center pt-2">
                      <span className="text-xs text-tech-500 font-medium">
                        +{sortedLessons.length - 8} more lessons
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Sidebar Footer */}
            <div className="p-4 border-t border-gray-200">
              <Link
                to="/courses"
                className="flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                All Courses
              </Link>
            </div>
          </div>
        </div>
      </>

      {/* Mobile Sidebar Toggle */}
      <button
        onClick={() => setSidebarOpen(true)}
        className="lg:hidden fixed top-20 left-4 z-30 p-2 sm:p-3 bg-white border border-gray-200 rounded-full shadow-lg hover:shadow-xl transition-all duration-200"
      >
        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {/* Main Content */}
      <div className="lg:ml-80">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
          {/* Breadcrumb */}
          <nav className="mb-6 lg:mb-8">
            <ol className="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm text-gray-500 overflow-x-auto">
              <li><Link to="/" className="hover:text-blue-600 whitespace-nowrap">Home</Link></li>
              <li><span>/</span></li>
              <li><Link to="/courses" className="hover:text-blue-600 whitespace-nowrap">Courses</Link></li>
              <li><span>/</span></li>
              <li className="text-gray-900 truncate">{course.title}</li>
            </ol>
          </nav>

        {/* Course Hero Section */}
        <div className="relative bg-gradient-to-br from-medical-900 via-medical-800 to-tech-900 rounded-2xl lg:rounded-3xl shadow-2xl overflow-hidden mb-8 lg:mb-12">
          {/* Medical Background Pattern */}
          <div className="absolute inset-0 opacity-5">
            <svg className="absolute top-4 right-4 lg:top-8 lg:right-8 w-24 h-24 lg:w-40 lg:h-40 text-white" fill="currentColor" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="35" fill="none" stroke="currentColor" strokeWidth="1"/>
              <circle cx="50" cy="50" r="20" fill="none" stroke="currentColor" strokeWidth="1"/>
              <circle cx="50" cy="50" r="8" fill="currentColor"/>
            </svg>
            <svg className="absolute bottom-4 left-4 lg:bottom-8 lg:left-8 w-20 h-20 lg:w-32 lg:h-32 text-white" fill="currentColor" viewBox="0 0 100 100">
              <path d="M20 50 Q50 20 80 50 Q50 80 20 50" fill="none" stroke="currentColor" strokeWidth="1"/>
            </svg>
          </div>
          
          <div className="relative p-6 sm:p-8 lg:p-10">
            <div className="flex flex-col items-start">
              {/* Course Info */}
              <div className="flex-1 text-white text-center lg:text-left">
                <div className="mb-4">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-medical-500/20 text-medical-100 border border-medical-400/30 mb-4">
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                    Medical Imaging Course
                  </span>
                </div>
                
                <h1 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 lg:mb-6 text-white leading-tight">
                  {course.title}
                </h1>
                
                <p className="text-base sm:text-lg lg:text-xl text-medical-100 mb-6 lg:mb-8 leading-relaxed max-w-4xl mx-auto lg:mx-0">
                  {course.description}
                </p>
                
                {/* Course Metadata with Icons and Tooltips */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 mb-6 lg:mb-8">
                  <div className="flex items-center space-x-3 bg-white/10 rounded-xl lg:rounded-2xl p-3 lg:p-4 backdrop-blur-sm">
                    <div className="w-10 h-10 lg:w-12 lg:h-12 bg-medical-500/30 rounded-lg lg:rounded-xl flex items-center justify-center">
                      <svg className="w-5 h-5 lg:w-6 lg:h-6 text-medical-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-xs lg:text-sm text-medical-200">👩‍🏫 Instructor</div>
                      <div className="text-sm lg:text-base font-semibold text-white truncate">{course.instructor?.full_name || 'Expert Radiologist'}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3 bg-white/10 rounded-xl lg:rounded-2xl p-3 lg:p-4 backdrop-blur-sm">
                    <div className="w-10 h-10 lg:w-12 lg:h-12 bg-tech-500/30 rounded-lg lg:rounded-xl flex items-center justify-center">
                      <svg className="w-5 h-5 lg:w-6 lg:h-6 text-tech-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-xs lg:text-sm text-tech-200">📦 Lessons</div>
                      <div className="text-sm lg:text-base font-semibold text-white">{course.lessons.length} comprehensive modules</div>
                    </div>
                  </div>
                  
                  {isEnrolled && progress ? (
                    <div className="flex items-center space-x-3 bg-white/10 rounded-2xl p-4 backdrop-blur-sm">
                      <div className="w-12 h-12 bg-green-500/30 rounded-xl flex items-center justify-center">
                        <svg className="w-6 h-6 text-green-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-sm text-green-200">📈 Progress</div>
                        <div className="font-semibold text-white">{progress.progress_percentage.toFixed(0)}% completed</div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-3 bg-white/10 rounded-2xl p-4 backdrop-blur-sm">
                      <div className="w-12 h-12 bg-yellow-500/30 rounded-xl flex items-center justify-center">
                        <svg className="w-6 h-6 text-yellow-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-sm text-yellow-200">💰 Price</div>
                        <div className="font-semibold text-white">${course.price}</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Progress Bar for Enrolled Users */}
                {isEnrolled && progress && (
                  <div className="bg-white/10 rounded-2xl p-6 backdrop-blur-sm">
                    <div className="flex justify-between text-sm text-medical-100 mb-3">
                      <span className="font-medium">Your Learning Progress</span>
                      <span className="font-semibold">{progress.completed_lessons} of {progress.total_lessons} lessons</span>
                    </div>
                    <div className="w-full bg-white/20 rounded-full h-4 shadow-inner">
                      <div 
                        className="bg-gradient-to-r from-green-400 via-medical-400 to-tech-400 h-4 rounded-full transition-all duration-700 shadow-lg" 
                        style={{ width: `${progress.progress_percentage}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-between text-xs text-medical-200 mt-2">
                      <span>Started</span>
                      <span className="font-medium">{progress.progress_percentage.toFixed(1)}% Complete</span>
                      <span>Mastery</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Course Structure - Table of Contents */}
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-tech-100">
          <div className="bg-gradient-to-r from-medical-600 via-medical-700 to-tech-700 px-10 py-8">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-display text-3xl font-bold text-white flex items-center">
                  <svg className="w-8 h-8 mr-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  Course Curriculum
                </h2>
                <p className="text-medical-100 mt-3 text-lg">
                  {course.lessons.length} comprehensive lessons • {isEnrolled ? 'Full Access Granted' : 'Enroll to unlock all content'}
                </p>
              </div>
              <div className="hidden md:block">
                <div className="bg-white/20 rounded-2xl p-4 backdrop-blur-sm">
                  <div className="text-2xl font-bold text-white">{course.lessons.length}</div>
                  <div className="text-sm text-medical-100">Lessons</div>
                </div>
              </div>
            </div>
          </div>
          
          {sortedLessons.length === 0 ? (
            <div className="text-center py-16 px-8">
              <div className="text-6xl mb-4">📚</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Course Content Coming Soon</h3>
              <p className="text-gray-500">The instructor is preparing the lessons for this course.</p>
            </div>
          ) : (
            <div className="p-4 sm:p-6 lg:p-8 space-y-4 lg:space-y-6">
              {sortedLessons.map((lesson, index) => {
                const isCompleted = progress ? progress.completed_lesson_ids.includes(lesson.id) : false;
                const isAccessible = isEnrolled;
                const isCurrentLesson = progress?.next_lesson === lesson.id;
                
                return (
                  <div key={lesson.id} className="group relative">
                    
                    {isAccessible ? (
                      <Link 
                        to={`/courses/${course.id}/lessons/${lesson.id}`}
                        className="block"
                      >
                        <div className={`bg-white rounded-xl lg:rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] border-2 ${
                          isCurrentLesson 
                            ? 'border-medical-400 bg-gradient-to-r from-medical-50 to-tech-50' 
                            : isCompleted 
                              ? 'border-green-300 hover:border-green-400' 
                              : 'border-tech-100 hover:border-medical-300'
                        }`}>
                          <div className="p-4 sm:p-6 lg:p-8">
                            <div className="flex items-start space-x-4 lg:space-x-6">
                              {/* Enhanced Lesson Number */}
                              <div className="flex-shrink-0 relative">
                                <div className={`w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-xl lg:rounded-2xl flex items-center justify-center text-base sm:text-lg font-bold shadow-lg ${
                                  isCompleted 
                                    ? 'bg-gradient-to-br from-green-500 to-green-600 text-white' 
                                    : isCurrentLesson
                                      ? 'bg-gradient-to-br from-medical-500 to-medical-600 text-white ring-4 ring-medical-200'
                                      : 'bg-gradient-to-br from-tech-100 to-tech-200 text-tech-700'
                                }`}>
                                  {isCompleted ? (
                                    <svg className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                  ) : (
                                    <span className="font-display">{lesson.order}</span>
                                  )}
                                </div>
                                {isCurrentLesson && (
                                  <div className="absolute -top-1 -right-1 lg:-top-2 lg:-right-2 w-5 h-5 lg:w-6 lg:h-6 bg-medical-500 rounded-full flex items-center justify-center">
                                    <svg className="w-2 h-2 lg:w-3 lg:h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                    </svg>
                                  </div>
                                )}
                              </div>
                              
                              <div className="flex-1 min-w-0">
                                {/* Lesson Header */}
                                <div className="flex items-start justify-between mb-3 lg:mb-4">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 mb-2">
                                      <h3 className="font-display text-lg sm:text-xl lg:text-2xl font-bold text-tech-900 group-hover:text-medical-700 transition-colors line-clamp-2">
                                        {lesson.title}
                                      </h3>
                                      <div className="flex space-x-2 mt-2 sm:mt-0">
                                        {isCurrentLesson && (
                                          <span className="bg-gradient-to-r from-medical-500 to-medical-600 text-white text-xs sm:text-sm font-medium px-2 py-1 sm:px-3 sm:py-1 rounded-full whitespace-nowrap">
                                            ▶ Continue
                                          </span>
                                        )}
                                        {isCompleted && (
                                          <span className="bg-gradient-to-r from-green-500 to-green-600 text-white text-xs sm:text-sm font-medium px-2 py-1 sm:px-3 sm:py-1 rounded-full whitespace-nowrap">
                                            ✅ Completed
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {/* Lesson Metadata */}
                                    <div className="flex flex-wrap items-center gap-2 lg:gap-4 text-xs sm:text-sm text-tech-500 mb-3 lg:mb-4">
                                      <div className="flex items-center">
                                        <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        ⏱️ 15-20 min
                                      </div>
                                      <div className="flex items-center">
                                        <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                        </svg>
                                        Interactive
                                      </div>
                                      <div className="flex items-center">
                                        <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 0h10a2 2 0 012 2v10a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2z" />
                                        </svg>
                                        Quiz included
                                      </div>
                                    </div>
                                  </div>
                                  
                                  <div className="flex items-center space-x-2 ml-2 lg:ml-4">
                                    <svg className="w-5 h-5 lg:w-6 lg:h-6 text-medical-400 group-hover:text-medical-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                  </div>
                                </div>
                                
                                {/* Lesson Description */}
                                {lesson.description && (
                                  <div className="mb-3 lg:mb-4">
                                    <p className="text-tech-600 leading-relaxed line-clamp-2 text-sm sm:text-base">
                                      {lesson.description}
                                    </p>
                                  </div>
                                )}
                                
                                {/* Lesson Preview/Skills */}
                                <div className="bg-gradient-to-r from-tech-50 to-medical-50 rounded-lg lg:rounded-xl p-3 lg:p-4">
                                  <div className="text-xs sm:text-sm text-tech-700 font-medium mb-2">What you'll learn:</div>
                                  <div className="text-xs sm:text-sm text-tech-600">
                                    Advanced diagnostic techniques, imaging protocols, and clinical applications in medical radiology.
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    ) : (
                      <div className="bg-gradient-to-br from-tech-50 to-tech-100 rounded-2xl shadow-md border-2 border-tech-200 opacity-75">
                        <div className="p-8">
                          <div className="flex items-start space-x-6">
                            {/* Locked Lesson Circle */}
                            <div className="flex-shrink-0 relative">
                              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-tech-200 to-tech-300 flex items-center justify-center text-2xl shadow-lg">
                                🔒
                              </div>
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between mb-4">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-3 mb-2">
                                    <h3 className="font-display text-2xl font-bold text-tech-700">
                                      {lesson.title}
                                    </h3>
                                    <span className="bg-tech-200 text-tech-700 text-sm font-medium px-3 py-1 rounded-full">
                                      🔒 Locked
                                    </span>
                                  </div>
                                  
                                  <div className="flex items-center space-x-4 text-sm text-tech-500 mb-4">
                                    <div className="flex items-center">
                                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                      </svg>
                                      ⏱️ 15-20 min
                                    </div>
                                    <div className="flex items-center">
                                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                      </svg>
                                      Premium Content
                                    </div>
                                  </div>
                                </div>
                              </div>
                              
                              {lesson.description && (
                                <div className="mb-4">
                                  <p className="text-tech-600 leading-relaxed line-clamp-2 text-base">
                                    {lesson.description}
                                  </p>
                                </div>
                              )}
                              
                              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-4 border border-yellow-200">
                                <div className="flex items-center text-sm text-yellow-800">
                                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                  </svg>
                                  <span className="font-medium">Enroll in this course to unlock this lesson and gain access to comprehensive medical imaging education.</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseDetails; 